import { useId } from 'react'
import { LAYOUT } from '../../../config/uiTokens'
import {
    FORT_STRENGTH,
    MISSILE_RISE_MS, MISSILE_RESPONSE_MS, MISSILE_MEET_OFFSET, MISSILE_EXCHANGE_MS,
    MISSILE_SURPLUS_RISE_MS, MISSILE_SURPLUS_STAGGER_MS, ROCKET_TRAVEL_MS,
    DRONE_FLIGHT_MS, DRONE_HOVER_MS,
} from '../../../rules/gameConfig'
import { ensureSlots, basesOf } from '../../../rules/gameLogic'
import { plotOf, subCoords } from '../../../rules/subplotGrid'
import { subCenter } from '../../../utils/hexGeometry'
import {
    SingleMissile, Explosion, HIT_SCATTER, SmokePlume, WashLabel, RocketInterceptor, INTERCEPT_FRACTION,
    DroneFlightGlyph, DRONE_FLIGHT_COLORS, FIRE_SWEEP_MS_PER_CELL, attackerSideOf,
} from '../battleFx'
import { R, H, BOARD_W, BOARD_H, plotTileBox } from './geometry'

// ─── Hex board — the reveal's battle layers ───────────────────────────────────
//
// The square board animates the reveal inside per-column overlays, positioning
// everything in percentages of a column and of the grid. The hex board does
// the same on its own geometry: a corridor is one pitch (4.5R) wide, a slot's
// missile launches from its tile centre, a landing shot flies into its aim
// hex's centre, and the halves meet at the band (50% of the board). The
// animated pieces themselves (missiles, bursts, smoke, interceptors, planes)
// are the shared battleFx primitives, so both boards play the same reveal.

const PITCH = 4.5 * R
export const corridorLeft = (c) => PITCH * c
const pctY = (y) => (y / BOARD_H) * 100
const pctXin = (x, c) => ((x - corridorLeft(c)) / PITCH) * 100
// The corridor's true middle: its centre hex column (and its drone tiles)
// sits 2.5R in from the strip's left edge, not at the strip's midpoint
// (2.25R) — a corridor's hexes span 5R, overlapping each neighbour by R/2.
export const CORRIDOR_MID = 2.5 * R
const MID_PCT = (CORRIDOR_MID / PITCH) * 100

// Per shot, where a side's volley launches from: each drone from its own
// tile, and a mature Base FORT_STRENGTH shots (its live-cell count) from the
// plot row its Grid footprint sits on — unless its broods held their fire
// this clash (a defence its flyers covered: the clash ledger's broodsFire,
// from resolveBattlefield). Constructions fire nothing.
export function shotOrigins(side, c, bf0) {
    if (!bf0) return []
    const bf = ensureSlots(bf0)
    const out = []
    bf.slots.forEach((t, p) => { if (t) out.push({ p, ...plotTileBox(side, c, p), unit: true }) })
    if (bf.broodsFire === false) return out
    const matureBases = basesOf(bf).filter(b => b.mature)
    for (const base of matureBases) {
        const shots = Math.max(0, Math.min(FORT_STRENGTH, base.hp ?? FORT_STRENGTH))
        const p = base.centerS != null ? plotOf(base.centerS).row : Math.max(0, (bf.maxArmyQty || 1) - 1)
        for (let m = 0; m < shots; m++) out.push({ p, ...plotTileBox(side, c, p), unit: false, centerS: base.centerS })
    }
    if (!matureBases.length && (bf.fortCount || 0) > 0) {
        const p = Math.max(0, (bf.maxArmyQty || 1) - 1)
        for (let m = 0; m < (bf.fortCount || 0) * FORT_STRENGTH; m++) out.push({ p, ...plotTileBox(side, c, p), unit: false })
    }
    return out
}

// The clash's missile volley for corridor c — the square board's MissileLayer
// on hex geometry. Plays as a SEQUENTIAL DUEL: exchange k, the attacker climbs
// shot k slowly toward the meeting point just past the band on the defender's
// side; the defender waits MISSILE_RESPONSE_MS then darts the short remaining
// gap so both arrive together and cancel. Once the duel is spent, the winner's
// surplus shots fire one at a time on to the aim hex — the first `absorbed` of
// them met partway by a defender rocket instead.
export function HexMissileLayer({
    c, selfStrength, oppStrength, winner, attackerSide = 'self', selfAttacking = false,
    targetPlotIndex, oppTargetPlotIndex, aimS = null, absorbed = 0, selfBf, oppBf,
}) {
    const numSelf   = selfStrength ?? 0
    const numOpp    = oppStrength  ?? 0
    const pairCount = Math.min(numSelf, numOpp)

    // Where a landing shot ends: the aim hex when the event carries one, else
    // the struck plot's tile centre (legacy events). The struck side is the
    // shooter's opposite.
    function landingPoint(shooter, targetIdx) {
        const struck = shooter === 'self' ? 'opp' : 'self'
        if (aimS != null) {
            const { x, y } = subCenter(struck, aimS, R)
            return { targetPct: pctY(y), leftPct: pctXin(x, c) }
        }
        const box = plotTileBox(struck, c, targetIdx ?? 0)
        return { targetPct: pctY(box.cy), leftPct: pctXin(box.cx, c) }
    }

    const selfOrigins = shotOrigins('self', c, selfBf)
    const oppOrigins  = shotOrigins('opp',  c, oppBf)

    const defenderSide = attackerSide === 'self' ? 'opp' : 'self'
    const meetPct = 50 + (defenderSide === 'self' ? MISSILE_MEET_OFFSET : -MISSILE_MEET_OFFSET)

    const missiles = [], explosions = [], interceptors = []
    const surplusStart = pairCount * MISSILE_EXCHANGE_MS

    function pushSide(side, count, origins, isWinner, targetIdx, role) {
        let excessSeen = 0
        for (let i = 0; i < count; i++) {
            const o = origins[i] ?? origins[origins.length - 1] ?? plotTileBox(side, c, Math.min(i, 4))
            const startPct = pctY(o.cy)
            const leftStartPct = pctXin(o.cx, c)
            const isExcess = i >= pairCount && isWinner && targetIdx !== null

            if (!isExcess) {
                const isDefender = role === 'defender'
                const launchDelay = i * MISSILE_EXCHANGE_MS + (isDefender ? MISSILE_RESPONSE_MS : 0)
                const travelDuration = isDefender ? Math.max(120, MISSILE_RISE_MS - MISSILE_RESPONSE_MS) : MISSILE_RISE_MS
                missiles.push({ key: `${side}${i}`, side, attacking: side === 'self' && selfAttacking, startPct, endPct: meetPct, leftStartPct, leftEndPct: MID_PCT, launchDelay, travelDuration, flightKind: 'rise' })
                if (role === 'attacker')
                    explosions.push({ key: `exp-cancel-${i}`, top: meetPct, leftPct: MID_PCT, delay: i * MISSILE_EXCHANGE_MS + MISSILE_RISE_MS, variant: 'cancel' })
                continue
            }

            const j = i - pairCount
            const { targetPct, leftPct } = landingPoint(side, targetIdx)
            const launchDelay = surplusStart + j * MISSILE_SURPLUS_STAGGER_MS

            if (excessSeen < absorbed) {
                excessSeen++
                const meet = targetPct + (50 - targetPct) * INTERCEPT_FRACTION
                const flownFrac = Math.abs(startPct - meet) / Math.max(1e-6, Math.abs(startPct - targetPct))
                const meetLeftPct = leftStartPct + (leftPct - leftStartPct) * flownFrac
                const missileDur = MISSILE_SURPLUS_RISE_MS * flownFrac
                const arrivalMs = launchDelay + missileDur
                missiles.push({ key: `${side}${i}`, side, attacking: side === 'self' && selfAttacking, startPct, endPct: meet, leftStartPct, leftEndPct: meetLeftPct, launchDelay, travelDuration: missileDur, flightKind: 'rise' })
                interceptors.push({
                    key: `rk-${side}${i}`, startPct: targetPct, endPct: meet, leftPct,
                    dir: targetPct < meet ? 'down' : 'up',
                    launchDelay: Math.max(0, arrivalMs - ROCKET_TRAVEL_MS),
                    travelDuration: Math.min(ROCKET_TRAVEL_MS, arrivalMs),
                })
                explosions.push({ key: `lp-${side}${i}`,  top: targetPct, leftPct, delay: Math.max(0, arrivalMs - ROCKET_TRAVEL_MS), variant: 'launch' })
                explosions.push({ key: `int-${side}${i}`, top: meet, leftPct: meetLeftPct, delay: arrivalMs, variant: 'intercept' })
                continue
            }

            const arrivalMs = launchDelay + MISSILE_SURPLUS_RISE_MS
            const [hx, hy] = HIT_SCATTER[j % HIT_SCATTER.length]
            missiles.push({ key: `${side}${i}`, side, attacking: side === 'self' && selfAttacking, startPct, endPct: targetPct, leftStartPct, leftEndPct: leftPct, launchDelay, travelDuration: MISSILE_SURPLUS_RISE_MS, flightKind: 'land' })
            explosions.push({ key: `exp-hit-${side}${i}`, top: targetPct, leftPct, delay: arrivalMs, variant: 'hit', dx: hx, dy: hy })
        }
    }

    pushSide('self', numSelf, selfOrigins, winner === 'self', targetPlotIndex,    attackerSide === 'self' ? 'attacker' : 'defender')
    pushSide('opp',  numOpp,  oppOrigins,  winner === 'opp',  oppTargetPlotIndex, attackerSide === 'opp'  ? 'attacker' : 'defender')

    return (
        <>
            <div className="bg__missile-layer">
                {missiles.map(({ key, ...m })     => <SingleMissile     key={key} {...m} />)}
                {interceptors.map(({ key, ...r }) => <RocketInterceptor key={key} {...r} />)}
                {explosions.filter(e => e.variant !== 'cancel').map(({ key, ...e }) => <Explosion key={key} {...e} />)}
            </div>
            <div className="bg__smoke-layer">
                {explosions.filter(e => e.variant === 'cancel').map(e =>
                    <SmokePlume key={e.key} top={e.top} leftPct={e.leftPct} delay={e.delay} />)}
            </div>
        </>
    )
}

// The reveal's corridor overlays: every corridor but the active one dims;
// the active one is left clear (or washed in its result colour), carries the
// WASH labels on a tie, and hosts the clash's missile layer while the duel
// plays. Sits above the tokens, click-through. The DIM is drawn on the
// corridors' own hexes (HexCorridorShade — one SVG over the board: each
// dimmed corridor's cells filled), not on the strips: the strips only place the missile
// layer and the wash labels.
export function HexBattleOverlays({
    selfBoard, oppBoard, activeClashId, clashResult, activeClashContext, activeClashWinner,
    activeClashSelfStrength, activeClashOppStrength, activeClashOppTargetIndex, activeClashSelfTargetIndex,
    activeClashAbsorbed, activeClashAimS, targetPlots, cells = null,
}) {
    const activeCol = activeClashId ? selfBoard.findIndex(bf => bf.id === activeClashId) : -1
    return (
        <div className="hexb__battle" data-testid="hex-battle">
            {cells && activeCol !== -1 && <HexCorridorShade cells={cells} activeCol={activeCol} />}
            {Array.from({ length: LAYOUT.GRID_COLS }, (_, col) => {
                const isActive = col === activeCol
                const isDimmed = activeCol !== -1 && !isActive
                const colClass = isDimmed ? 'bg__col-overlay--dimmed'
                    : isActive && clashResult ? `bg__col-overlay--${clashResult.toLowerCase()}`
                    : isActive ? 'bg__col-overlay--active'
                    : ''
                const targetPlotIndex = isActive
                    ? ((targetPlots?.[col] ?? null) !== null ? targetPlots[col] : (activeClashSelfTargetIndex ?? null))
                    : null
                const left = (corridorLeft(col) / BOARD_W) * 100
                const width = col === LAYOUT.GRID_COLS - 1 ? 100 - left : (PITCH / BOARD_W) * 100
                return (
                    <div
                        key={col}
                        className={`bg__col-overlay hexb__corridor ${colClass}`}
                        style={{ left: `${left}%`, width: `${width}%` }}
                        data-corridor={col}
                        data-active={isActive ? 'true' : undefined}
                    >
                        <div className="bg__col-half bg__col-half--opp" data-side="opp" />
                        <div className="bg__col-half bg__col-half--self" data-side="self" />
                        {/* One WASH, dead centre of the corridor: on the band, over its
                            middle hex column (CORRIDOR_MID — not the strip's midpoint,
                            and the last strip runs on to the board's edge). */}
                        {isActive && clashResult === 'TIE' && (
                            <WashLabel leftPct={CORRIDOR_MID / (BOARD_W * width / 100) * 100} />
                        )}
                        {isActive && !clashResult && activeClashContext && (
                            <HexMissileLayer
                                c={col}
                                selfStrength={activeClashSelfStrength}
                                oppStrength={activeClashOppStrength}
                                winner={activeClashWinner}
                                attackerSide={attackerSideOf(activeClashContext, activeClashWinner)}
                                selfAttacking={activeClashContext === 'self_attacked' || activeClashContext === 'both_attacked'}
                                targetPlotIndex={targetPlotIndex}
                                oppTargetPlotIndex={activeClashOppTargetIndex ?? null}
                                aimS={activeClashAimS ?? null}
                                absorbed={activeClashAbsorbed ?? 0}
                                selfBf={selfBoard[col]}
                                oppBf={oppBoard[col]}
                            />
                        )}
                    </div>
                )
            })}
        </div>
    )
}

// The fire sweep's ring, expanding from the crater's aim hex across the
// struck half at FIRE_SWEEP_MS_PER_CELL per cell of radius (the same clock
// the fresh craters fade in on), overshooting the farthest crater, then out.
export function HexFireRing({ side, aimS, maxDist, onDone }) {
    const { x, y } = subCenter(side, aimS, R)
    const radiusCells = maxDist * 1.15 + 2
    const radiusPx = radiusCells * H
    const halfTop = side === 'opp' ? 0 : BOARD_H / 2
    return (
        <div className={`bg__fire-layer bg__fire-layer--${side}`}>
            <div
                className="bg__fire-ring"
                style={{
                    left:   `${(x / BOARD_W) * 100}%`,
                    top:    `${((y - halfTop) / (BOARD_H / 2)) * 100}%`,
                    width:  `${(2 * radiusPx / BOARD_W) * 100}%`,
                    height: `${(2 * radiusPx / (BOARD_H / 2)) * 100}%`,
                    animationDuration: `${Math.round(radiusCells * FIRE_SWEEP_MS_PER_CELL)}ms`,
                }}
                onAnimationEnd={onDone}
            />
        </div>
    )
}

// A rising mushroom cloud over every structure cell destroyed by the latest
// clash, each waiting for the fire front to reach its cell.
export function HexBlastClouds({ cells }) {
    if (!cells?.length) return null
    return (
        <div className="hexb__blasts">
            {cells.map(({ key, cx, cy, delay }) => (
                <div key={key} className="hexb__blast-slot" style={{
                    left: `${((cx - R) / BOARD_W) * 100}%`, top: `${((cy - H / 2) / BOARD_H) * 100}%`,
                    width: `${(2 * R / BOARD_W) * 100}%`, height: `${(H / BOARD_H) * 100}%`,
                }}>
                    <span className="bg__ps-blast" style={{ animationDelay: `${delay}ms` }}>
                        <span className="bg__ps-blast-stem" />
                        <span className="bg__ps-blast-cap" />
                    </span>
                </div>
            ))}
        </div>
    )
}

// Reveal-phase recon planes. Yours lifts off from its launch structure and
// flies to its target across the band; the opponent's enters at the band
// over its corridor and descends to its target (the launch cell is never
// disclosed). Each hovers over its target while the scan sweeps in, then fades.
export function HexDroneFlights({ flights }) {
    if (!flights) return null
    const pct = (side, s) => { const { x, y } = subCenter(side, s, R); return { x: (x / BOARD_W) * 100, y: (y / BOARD_H) * 100 } }
    const drones = []
    for (const [i, f] of (flights.self ?? []).entries()) {
        if (f.targetS == null) continue
        const from = f.launchS != null ? pct('self', f.launchS) : { x: pct('opp', f.targetS).x, y: 50 }
        const to = pct('opp', f.targetS)
        drones.push({ key: `self-${i}`, side: 'self', x0: from.x, y0: from.y, x1: to.x, y1: to.y })
    }
    for (const [i, f] of (flights.opp ?? []).entries()) {
        if (f.targetS == null) continue
        const to = pct('self', f.targetS)
        // Straight down its corridor from the band: enter over the target's column.
        const entryX = ((subCoords(f.targetS).sc * 1.5 * R + R) / BOARD_W) * 100
        drones.push({ key: `opp-${i}`, side: 'opp', x0: entryX, y0: 50, x1: to.x, y1: to.y })
    }
    if (!drones.length) return null
    return (
        <div className="bg__drone-layer">
            {drones.map(d => (
                <div
                    key={d.key}
                    className={`bg__drone bg__drone--${d.side}`}
                    style={{
                        '--dr-x0': `${d.x0}%`, '--dr-y0': `${d.y0}%`,
                        '--dr-x1': `${d.x1}%`, '--dr-y1': `${d.y1}%`,
                        '--dr-fly-ms': `${DRONE_FLIGHT_MS}ms`,
                        '--dr-hover-ms': `${DRONE_HOVER_MS}ms`,
                    }}
                >
                    <span className="bg__drone-body">
                        <DroneFlightGlyph {...DRONE_FLIGHT_COLORS[d.side]} />
                    </span>
                    <span className="bg__drone-scanbeam" />
                </div>
            ))}
        </div>
    )
}

// The corridor dim on hex geometry: every corridor but the clash's has its
// cells (both halves) washed in the square board's dim
// (.bg__col-overlay--dimmed: rgba(0,0,0,0.66)), so the dim's edge is the
// zigzag of the corridor's own hexes; the clash's corridor is simply left
// undimmed (no outline). Fades up in a beat (.hexb__shade).
//
// Mobile browsers shimmered its edges 1–2px sideways during the clash
// animations, so it is built to sit still: ONE path (every dimmed cell as a
// subpath — no per-hex edges to re-blend), scaled exactly as the board's own
// SVG is (full width, height from the viewBox — never stretched), and kept
// on a fixed compositor layer (HexBoard.css .hexb__shade).
//
// Its strength fades in from the clash corridor: at the corridor's outer
// reach (the furthest its zigzag edge pushes out) the dim is only
// SHADE_EDGE of full, rising to full SHADE_FADE_W further out, so the
// jagged inner edges are soft and the board darkens with distance.
export const CORRIDOR_DIM = 'rgba(0, 0, 0, 0.66)'
const SHADE_OPACITY = 0.66
const SHADE_EDGE = 0.15
const SHADE_FADE_W = 4.5 * R            // about one corridor's width
export function HexCorridorShade({ cells, activeCol }) {
    const grad = `hexb-shade-fade-${useId().replace(/:/g, '')}`
    // The clash corridor's horizontal reach, from its own hexes' corners.
    let x0 = Infinity, x1 = -Infinity
    for (const c of cells.opp) {
        if (plotOf(c.s).col !== activeCol) continue
        x0 = Math.min(x0, c.cx - R)
        x1 = Math.max(x1, c.cx + R)
    }
    const at = (x) => Math.min(1, Math.max(0, x / BOARD_W)).toFixed(4)
    const stops = [
        [0, 1], [at(x0 - SHADE_FADE_W), 1], [at(x0), SHADE_EDGE],
        [at(x1), SHADE_EDGE], [at(x1 + SHADE_FADE_W), 1], [1, 1],
    ]
    let d = ''
    for (let col = 0; col < LAYOUT.GRID_COLS; col++) {
        if (col === activeCol) continue
        for (const side of ['opp', 'self']) {
            for (const c of cells[side]) {
                if (plotOf(c.s).col === col) d += `M${c.pts.trim().split(/\s+/).join('L')}Z`
            }
        }
    }
    return (
        <svg className="hexb__shade" viewBox={`0 0 ${BOARD_W} ${BOARD_H}`} width="100%"
            data-testid="hex-shade" data-active-col={activeCol} aria-hidden="true">
            <defs>
                <linearGradient id={grad} gradientUnits="userSpaceOnUse" x1="0" y1="0" x2={BOARD_W} y2="0">
                    {stops.map(([o, k], i) => <stop key={i} offset={o} stopColor="#000" stopOpacity={(SHADE_OPACITY * k).toFixed(3)} />)}
                </linearGradient>
            </defs>
            <path d={d} fill={`url(#${grad})`} data-dim="true" />
        </svg>
    )
}
